// [Codex 2026-07-16] User-owned provider connections are the normal chat credential boundary.
import { useCallback, useEffect, useMemo, useState } from "react";
import { getAccessToken as getDefaultAccessToken } from "../../../lib/supabase";
import { API_BASE_URL } from "../constants";
import {
  providerStatusKey,
  writeStoredProviderModel,
  type HarperProvider,
} from "../provider-options";

export interface ProviderConnectionState {
  provider: string;
  authType: string;
  status: "ready" | "pending" | "expired" | "invalid" | "disconnected";
  maskedCredential: string;
  accountLabel: string | null;
  lastError: string | null;
  model: string | null;
  models: string[];
}

export interface ProviderSetupDefinition {
  id: string;
  authTypes: string[];
  signupUrl: string;
  setupHint: string;
  defaultModel: string;
  models: string[];
  recommended?: boolean;
}

type AccessTokenProvider = () => Promise<string | null>;

interface DesktopProviderBridge {
  providerAuth?: {
    syncOpenAiToCloud?: (
      accessToken: string,
    ) => Promise<{ ok?: boolean; error?: string }>;
  };
}

function readDesktopProviderBridge(): DesktopProviderBridge | null {
  if (typeof window === "undefined") return null;
  const runtimeWindow = window as Window & {
    electron?: DesktopProviderBridge;
  };
  return runtimeWindow.electron ?? null;
}

export function useProviderConnections(
  active: boolean,
  getAccessToken: AccessTokenProvider = getDefaultAccessToken,
  apiBase = API_BASE_URL,
) {
  const [connections, setConnections] = useState<ProviderConnectionState[]>([]);
  const [definitions, setDefinitions] = useState<ProviderSetupDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await providerFetch(
        "/api/settings/provider-connections",
        {},
        getAccessToken,
        apiBase,
      );
      if (!response.ok) throw new Error(await responseError(response));
      const body = await response.json();
      const nextConnections = (body.connections ??
        []) as ProviderConnectionState[];
      setConnections(nextConnections);
      setDefinitions((body.providers ?? []) as ProviderSetupDefinition[]);
      syncCachedStatuses(nextConnections);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Provider status failed.",
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAccessToken]);

  useEffect(() => {
    if (active) void refresh();
  }, [active, refresh]);

  const byProvider = useMemo(() => {
    const map = new Map<HarperProvider, ProviderConnectionState>();
    connections.forEach((connection) => {
      const provider = fromBackendProvider(connection.provider);
      if (provider) map.set(provider, connection);
    });
    return map;
  }, [connections]);

  const saveApiKey = useCallback(
    async (input: {
      provider: HarperProvider;
      credential: string;
      baseUrl?: string;
      model?: string;
    }) => {
      const response = await providerFetch(
        "/api/settings/provider-connections",
        {
          method: "POST",
          body: JSON.stringify({
            provider: toBackendProvider(input.provider),
            authType: "api_key",
            credential: input.credential,
            baseUrl: input.baseUrl || undefined,
            model: input.model || undefined,
          }),
        },
        getAccessToken,
        apiBase,
      );
      if (!response.ok) throw new Error(await responseError(response));
      await refresh();
    },
    [apiBase, getAccessToken, refresh],
  );

  const selectModel = useCallback(
    async (provider: HarperProvider, model: string) => {
      const id = toBackendProvider(provider);
      const response = await providerFetch(
        `/api/settings/provider-connections/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify({ model }) },
        getAccessToken,
        apiBase,
      );
      if (!response.ok) throw new Error(await responseError(response));
      writeStoredProviderModel(provider, model);
      await refresh();
    },
    [apiBase, getAccessToken, refresh],
  );

  const importDesktop = useCallback(
    async (provider: HarperProvider) => {
      const bridge = readDesktopProviderBridge();
      if (!bridge?.providerAuth?.syncOpenAiToCloud) {
        throw new Error(
          "Desktop credential import is only available in Fintheon Desktop.",
        );
      }
      const token = await getAccessToken();
      if (!token)
        throw new Error("Sign in to Fintheon before connecting OpenAI.");
      const synced = await bridge.providerAuth.syncOpenAiToCloud(token);
      if (!synced?.ok) {
        throw new Error(
          synced?.error ||
            "Fintheon Desktop could not sync the OpenAI connection.",
        );
      }
      const id = toBackendProvider(provider);
      const cloudVerification = await providerFetch(
        `/api/settings/provider-connections/${encodeURIComponent(id)}/recheck`,
        { method: "POST" },
        getAccessToken,
        apiBase,
      );
      if (!cloudVerification.ok) {
        throw new Error(
          "OpenAI was saved on Desktop, but cloud verification failed. Reconnect before using it on mobile.",
        );
      }
      await refresh();
    },
    [apiBase, getAccessToken, refresh],
  );

  const disconnect = useCallback(
    async (provider: HarperProvider) => {
      const id = toBackendProvider(provider);
      const response = await providerFetch(
        `/api/settings/provider-connections/${encodeURIComponent(id)}`,
        { method: "DELETE" },
        getAccessToken,
        apiBase,
      );
      if (!response.ok) throw new Error(await responseError(response));
      await refresh();
    },
    [apiBase, getAccessToken, refresh],
  );

  const recheck = useCallback(
    async (provider: HarperProvider) => {
      const id = toBackendProvider(provider);
      const response = await providerFetch(
        `/api/settings/provider-connections/${encodeURIComponent(id)}/recheck`,
        { method: "POST" },
        getAccessToken,
        apiBase,
      );
      if (!response.ok) throw new Error(await responseError(response));
      await refresh();
    },
    [apiBase, getAccessToken, refresh],
  );

  return {
    byProvider,
    definitions,
    loading,
    error,
    refresh,
    saveApiKey,
    importDesktop,
    disconnect,
    recheck,
    selectModel,
  };
}

async function providerFetch(
  path: string,
  init: RequestInit = {},
  getAccessToken: AccessTokenProvider = getDefaultAccessToken,
  base = API_BASE_URL,
): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(`${base}${path}`, {
    ...init,
    headers,
    signal: init.signal ?? AbortSignal.timeout(45_000),
  });
}

async function responseError(response: Response): Promise<string> {
  const body = await response.json().catch(() => null);
  return (
    body?.details ||
    body?.error ||
    `Provider request failed (${response.status}).`
  );
}

function syncCachedStatuses(connections: ProviderConnectionState[]): void {
  connections.forEach((connection) => {
    const provider = fromBackendProvider(connection.provider);
    if (!provider) return;
    localStorage.setItem(
      providerStatusKey(provider),
      connection.status === "ready" ? "set" : connection.status,
    );
    if (connection.model) {
      writeStoredProviderModel(provider, connection.model);
    }
  });
}

export function toBackendProvider(provider: HarperProvider): string {
  return provider;
}

function fromBackendProvider(provider: string): HarperProvider | null {
  if (
    provider === "nous" ||
    provider === "openai" ||
    provider === "anthropic" ||
    provider === "moonshot"
  ) {
    return provider;
  }
  return null;
}
